/* SIRDAB navigation and consistent scroll motion across mobile browsers. */
(() => {
  const controllers = new WeakMap();
  // Use the same scroll math in Safari and Chrome; no CSS timeline dependency.
  function initializeJourney(root, signal) {
    const intro = root.querySelector('.intro-journey');
    const hero = root.querySelector('.hero-scene');
    const anatomy = root.querySelector('.anatomy-journey');
    const scene = root.querySelector('.anatomy-scene');
    if (!intro || !hero || !anatomy || !scene) return () => {};
    const copy = root.querySelector('.hero-copy');
    const object = root.querySelector('.hero-object');
    const story = root.querySelector('.hero-story');
    const aside = root.querySelector('.hero-aside');
    const watermark = root.querySelector('.hero-watermark');
    const heroCap = object?.querySelector('.cap-piece');
    const cap = anatomy.querySelector('.cap-piece');
    const pump = anatomy.querySelector('.pump-piece');
    const body = anatomy.querySelector('.body-piece');
    const labels = [...anatomy.querySelectorAll('.anatomy-label')];
    const arrows = [...anatomy.querySelectorAll('.annotation-arrow')];
    const steps = [...anatomy.querySelectorAll('.anatomy-progress > span')];
    const elements = [copy, object, story, aside, watermark, heroCap, cap, pump, body, ...labels, ...arrows, ...steps].filter(Boolean);
    const originalStyles = new Map(elements.map(element => [element, element.getAttribute('style')]));
    let frame = 0;
    let active = false;
    const clamp = value => Math.max(0, Math.min(1, value));
    const range = (value, from, to) => clamp((value - from) / (to - from));
    const ease = value => value * value * (3 - 2 * value);
    const style = (element, property, value) => { if (element) element.style[property] = value; };
    function restore() {
      originalStyles.forEach((value, element) => {
        if (value === null) element.removeAttribute('style');
        else element.setAttribute('style', value);
      });
    }
    function render() {
      frame = 0;
      if (signal.aborted) return;
      const enabled = !root.classList.contains('motion-paused') && window.innerHeight >= 560;
      if (enabled !== active) {
        active = enabled;
        root.classList.toggle('journey-motion-active', active);
        if (!active) restore();
      }
      if (!active) return;
      const mobile = window.matchMedia('(max-width: 700px)').matches;
      const opening = clamp(-intro.getBoundingClientRect().top / Math.max(1, intro.offsetHeight - hero.offsetHeight));
      const detail = clamp(-anatomy.getBoundingClientRect().top / Math.max(1, anatomy.offsetHeight - scene.offsetHeight));
      const fade = range(opening, .12, .37);
      const reveal = range(opening, .43, .72);
      const travel = ease(range(opening, mobile ? .24 : .12, .75));
      const lift = ease(range(opening, .12, .36)) * (1 - ease(range(opening, .52, .78)));
      style(copy, 'opacity', String(1 - fade));
      style(copy, 'transform', 'translateY(' + (-35 * fade) + 'px)');
      style(copy, 'filter', 'blur(' + (4 * fade) + 'px)');
      style(copy, 'visibility', fade === 1 ? 'hidden' : 'visible');
      style(object, 'left', ((mobile ? 50 : 65) - (mobile ? 27 : 37) * travel) + '%');
      style(object, 'transform', 'translate(-50%, -50%) scale(' + (mobile ? 1 - .14 * travel : 1) + ')');
      if (mobile) style(object, 'top', ((window.innerHeight <= 650 ? 72 : 70) - 10 * travel) + '%');
      else object?.style.removeProperty('top');
      style(story, 'opacity', String(reveal));
      style(story, 'transform', mobile ? 'translateY(' + (24 * (1 - reveal)) + 'px)' : 'translateY(calc(-40% + ' + (30 * (1 - reveal)) + 'px))');
      style(story, 'filter', 'blur(' + (4 * (1 - reveal)) + 'px)');
      style(aside, 'opacity', String(1 - range(opening, .15, .33)));
      style(watermark, 'transform', 'translateX(' + (-3 * opening) + '%)');
      style(heroCap, 'transform', 'translateY(' + (-10 * lift) + '%) rotate(' + (-3 * lift) + 'deg)');
      const capProgress = range(detail, .08, .28);
      style(cap, 'transform', 'translateY(' + (-30 * capProgress) + '%) rotate(' + (-7 * capProgress) + 'deg)');
      style(pump, 'transform', 'translateY(' + (-73 * range(detail, .28, .48)) + '%)');
      style(body, 'transform', 'translateY(' + (8 * range(detail, .48, .68)) + '%)');
      labels.forEach((label, index) => {
        const visible = range(detail, .14 + index * .2, .27 + index * .2);
        style(label, 'opacity', String(visible));
        style(label, 'transform', 'translateY(' + (12 * (1 - visible)) + 'px)');
        style(arrows[index], 'opacity', String(visible));
        style(arrows[index], 'strokeDasharray', '150');
        style(arrows[index], 'strokeDashoffset', String(150 * (1 - visible)));
        style(steps[index], 'opacity', String(.2 + .8 * visible));
      });
    }
    function schedule() { if (!frame && !signal.aborted) frame = requestAnimationFrame(render); }
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    window.addEventListener('pageshow', schedule, { signal });
    const observer = new ResizeObserver(schedule);
    observer.observe(root);
    observer.observe(hero);
    observer.observe(scene);
    signal.addEventListener('abort', () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      root.classList.remove('journey-motion-active');
      restore();
    }, { once: true });
    return schedule;
  }

  function initialize(root) {
    if (controllers.has(root)) return;
    const controller = new AbortController();
    controllers.set(root, controller);
    const signal = controller.signal;
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    const collection = root.querySelector('#collection');
    const motionButton = root.querySelector('.motion-control');
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let paused = preference.matches;
    const scheduleJourney = initializeJourney(root, signal);
    function updateMotion() {
      root.classList.toggle('motion-paused', paused || preference.matches);
      scheduleJourney();
      if (motionButton) {
        motionButton.disabled = preference.matches;
        motionButton.setAttribute('aria-pressed', String(paused || preference.matches));
        motionButton.textContent = preference.matches ? 'Reduced motion' : paused ? 'Resume motion' : 'Pause motion';
      }
    }
    function select(tab, moveFocus = false) {
      tabs.forEach(item => {
        const active = item === tab;
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
        item.toggleAttribute('data-active', active);
      });
      panels.forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      collection.dataset.scent = tab.dataset.scentKey;
      if (moveFocus) tab.focus();
    }
    root.addEventListener('click', event => {
      const tab = event.target.closest('[role="tab"]');
      if (tab && tabs.includes(tab)) select(tab);
      if (event.target.closest('.motion-control') && !preference.matches) { paused = !paused; updateMotion(); }
      if (event.target.closest('.export-mobile-nav a')) root.querySelector('.mobile-menu-disclosure')?.removeAttribute('open');
    }, { signal });
    root.addEventListener('keydown', event => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
      event.preventDefault();
      let index = tabs.indexOf(tab);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      select(tabs[index], true);
    }, { signal });
    preference.addEventListener('change', () => { paused = preference.matches; updateMotion(); }, { signal });
    updateMotion();
  }
  document.documentElement.classList.replace('no-js', 'js');
  document.querySelectorAll('.sirdab-experience').forEach(initialize);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.sirdab-experience').forEach(initialize));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.sirdab-experience').forEach(root => controllers.get(root)?.abort()));
})();
